// Content Studio API Client
// Provides methods to interact with the Content Studio edge function

import { supabase } from '@/integrations/supabase/client';
import { captureContentStudioError } from '@/lib/sentry';
import type {
  DocumentInput,
  PodcastConfig,
  QuizConfig,
  SlideConfig,
  GenerationConfig,
  ContentStudioResponse,
  DocumentAnalysis,
  PodcastScript,
  PodcastResult,
  PodcastEstimate,
  SlidesDeck,
  StudyGuide,
  Quiz,
  QuizQuestion,
  UserAnswer,
  QuizGradingResult,
  FlashcardDeck,
  MindMap,
  SummaryResponse,
  FAQResponse,
  Timeline,
  BriefingDoc,
} from './types';

// URL fetch result type
interface UrlFetchResult {
  url: string;
  title: string;
  content: string;
  wordCount: number;
  fetchedAt: string;
}

const FUNCTION_NAME = 'content-studio';

/**
 * Base method to call the Content Studio edge function
 */
async function callContentStudio<T>(
  action: string,
  document?: DocumentInput,
  config?: PodcastConfig | QuizConfig | SlideConfig,
  generationConfig?: GenerationConfig,
  additionalData?: Record<string, unknown>
): Promise<T> {
  const { data, error } = await supabase.functions.invoke(FUNCTION_NAME, {
    body: {
      action,
      document,
      config,
      generationConfig,
      ...additionalData,
    },
  });

  if (error) {
    console.error('Content Studio API error:', error);
    captureContentStudioError(error, { action, generationType: config ? 'with_config' : 'basic' });
    throw new Error(error.message || 'Failed to call Content Studio');
  }

  const response = data as ContentStudioResponse<T>;

  if (!response.success) {
    captureContentStudioError(new Error(response.error || 'Content Studio request failed'), { action });
    throw new Error(response.error || 'Content Studio request failed');
  }

  return response.data as T;
}

/**
 * Content Studio API Client
 */
export const contentStudioClient = {
  /**
   * Generic method to call any content studio action with optional background generation ID.
   * When generationId is provided, the Edge Function tracks the generation lifecycle
   * (pending → processing → completed/failed) in the database.
   */
  async callAction<T>(
    action: string,
    document: DocumentInput,
    options?: {
      config?: PodcastConfig | QuizConfig | SlideConfig | Record<string, unknown>;
      generationConfig?: GenerationConfig;
      generationId?: string | null;
    }
  ): Promise<T> {
    const additionalData: Record<string, unknown> = {};
    if (options?.generationId) {
      additionalData.generationId = options.generationId;
    }
    return callContentStudio<T>(
      action,
      document,
      options?.config as PodcastConfig | QuizConfig | SlideConfig | undefined,
      options?.generationConfig,
      Object.keys(additionalData).length > 0 ? additionalData : undefined
    );
  },

  /**
   * Analyze a document and extract key information
   */
  async analyze(document: DocumentInput): Promise<DocumentAnalysis> {
    return callContentStudio<DocumentAnalysis>('analyze', document);
  },

  /**
   * Generate a full podcast with audio
   * If config.isVideoPodcast is true, generates a video podcast with avatars
   */
  async generatePodcast(
    document: DocumentInput,
    config?: PodcastConfig
  ): Promise<PodcastResult> {
    const action = config?.isVideoPodcast ? 'video-podcast' : 'podcast';
    return callContentStudio<PodcastResult>(action, document, config);
  },

  /**
   * Generate a podcast script without audio (faster)
   */
  async generatePodcastScript(
    document: DocumentInput,
    config?: PodcastConfig
  ): Promise<PodcastScript> {
    return callContentStudio<PodcastScript>('podcast-script', document, config);
  },

  /**
   * Estimate podcast generation time and cost
   */
  async estimatePodcast(config?: PodcastConfig): Promise<PodcastEstimate> {
    return callContentStudio<PodcastEstimate>('estimate-podcast', undefined, config);
  },

  /**
   * Generate a slide deck from content
   */
  async generateSlides(
    document: DocumentInput,
    config?: SlideConfig
  ): Promise<SlidesDeck> {
    return callContentStudio<SlidesDeck>('slides', document, config);
  },

  /**
   * Generate a study guide from content
   */
  async generateStudyGuide(document: DocumentInput): Promise<StudyGuide> {
    return callContentStudio<StudyGuide>('study-guide', document);
  },

  /**
   * Generate a quiz from content
   */
  async generateQuiz(
    document: DocumentInput,
    config?: QuizConfig
  ): Promise<Quiz> {
    return callContentStudio<Quiz>('quiz', document, config);
  },

  /**
   * Grade a completed quiz
   */
  async gradeQuiz(
    questions: QuizQuestion[],
    answers: UserAnswer[]
  ): Promise<QuizGradingResult> {
    return callContentStudio<QuizGradingResult>('grade-quiz', undefined, undefined, undefined, {
      questions,
      answers,
    });
  },

  // =====================================================
  // NotebookLM-style Features
  // =====================================================

  /**
   * Generate flashcards from content
   */
  async generateFlashcards(
    document: DocumentInput,
    cardCount: number = 20,
    generationConfig?: GenerationConfig
  ): Promise<FlashcardDeck> {
    return callContentStudio<FlashcardDeck>('flashcards', document, { cardCount } as unknown as QuizConfig, generationConfig);
  },

  /**
   * Generate a mind map from content
   */
  async generateMindMap(document: DocumentInput, generationConfig?: GenerationConfig): Promise<MindMap> {
    return callContentStudio<MindMap>('mind-map', document, undefined, generationConfig);
  },

  /**
   * Generate a summary from content
   */
  async generateSummary(document: DocumentInput, generationConfig?: GenerationConfig): Promise<SummaryResponse> {
    return callContentStudio<SummaryResponse>('summary', document, undefined, generationConfig);
  },

  /**
   * Generate FAQ from content
   */
  async generateFAQ(
    document: DocumentInput,
    questionCount: number = 10,
    generationConfig?: GenerationConfig
  ): Promise<FAQResponse> {
    return callContentStudio<FAQResponse>('faq', document, { questionCount } as unknown as QuizConfig, generationConfig);
  },

  /**
   * Generate a timeline from content
   */
  async generateTimeline(document: DocumentInput): Promise<Timeline> {
    return callContentStudio<Timeline>('timeline', document);
  },

  /**
   * Generate a briefing document from content
   */
  async generateBriefingDoc(document: DocumentInput): Promise<BriefingDoc> {
    return callContentStudio<BriefingDoc>('briefing-doc', document);
  },

  /**
   * Fetch and extract content from a URL
   */
  async fetchUrl(url: string): Promise<UrlFetchResult> {
    return callContentStudio<UrlFetchResult>('fetch-url', { content: url });
  },
};

// Export individual functions for convenience
export const {
  analyze,
  generatePodcast,
  generatePodcastScript,
  estimatePodcast,
  generateSlides,
  generateStudyGuide,
  generateQuiz,
  gradeQuiz,
  generateFlashcards,
  generateMindMap,
  generateSummary,
  generateFAQ,
  generateTimeline,
  generateBriefingDoc,
  fetchUrl,
} = contentStudioClient;

// Default export
export default contentStudioClient;
